import React from 'react';
import { CheckIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../../components/ui/_cn';
import FinDropdown from '../../../connect/finance/fin-dropdown';
import { CHANNELS } from './ChannelIcons';
import { ALL_STORES } from '../../lib/campaign-rule';

// Handoff §2 step 3: a collapsed "› Advanced settings" row (the chevron
// turns 90° open) over Store (Optional), Channels and Menus (Optional).
// Defaults are everything: All stores, three channels, All menus; one
// channel always stays on. Edit opens it by default (§7). The Store row is
// left out for a one-store company (spec §2.4, D3).
export const ADVANCED_COPY = {
  title: 'Advanced settings',
  store: 'Store',
  channels: 'Channels',
  menus: 'Menus',
  optional: 'Optional',
  allStores: 'All stores',
  allMenus: 'All menus',
};

const FieldTitle = ({ children, optional = false }) => (
  <p className="text-[13px] font-semibold text-fin-ink">
    {children}
    {optional ? <span className="ml-1.5 font-medium text-fin-faint">{ADVANCED_COPY.optional}</span> : null}
  </p>
);

const AdvancedSettings = ({
  open,
  onToggle,
  showStore = true,
  storeOptions = [],
  storeid = null,
  onStore,
  platforms = [],
  onPlatforms,
  menus = [],
  menuids = [],
  onMenus,
  collapsedNote = '',
  disabled = false,
}) => {
  // A menu id the list does not know is shown as it is (coupon-menus.js).
  const menuLabel = menuids.length
    ? menuids.map((id) => menus.find((menu) => menu.id === id)?.name || id).join(', ')
    : undefined;

  return (
    <div className="border-t border-fin-hair pt-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="flex items-center gap-1.5 py-1 text-left text-[13.5px] font-semibold text-fin-ink"
        >
          <ChevronRightIcon
            className={cn('h-3.5 w-3.5 shrink-0 stroke-[2.5] text-fin-muted transition-transform', open && 'rotate-90')}
            aria-hidden="true"
          />
          {ADVANCED_COPY.title}
        </button>
        {!open && collapsedNote ? (
          <span className="ml-auto truncate text-[12.5px] font-medium text-fin-muted">{collapsedNote}</span>
        ) : null}
      </div>

      {open ? (
        <div className="mt-3 space-y-3.5">
          {showStore ? (
            <div className="space-y-1.5">
              <FieldTitle optional>{ADVANCED_COPY.store}</FieldTitle>
              <FinDropdown
                block
                size="md"
                mode="radio"
                aria-label={ADVANCED_COPY.store}
                value={storeid ?? ALL_STORES}
                options={storeOptions}
                placeholder={ADVANCED_COPY.allStores}
                disabled={disabled}
                onChange={(value) => onStore(value === ALL_STORES ? null : value)}
              />
            </div>
          ) : null}

          <div className="space-y-1.5">
            <FieldTitle>{ADVANCED_COPY.channels}</FieldTitle>
            <div role="group" aria-label={ADVANCED_COPY.channels} className="flex flex-wrap gap-2">
              {CHANNELS.map(({ value, label }) => {
                const on = platforms.includes(value);
                const last = on && platforms.length === 1;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={on}
                    aria-disabled={last || undefined}
                    disabled={disabled}
                    onClick={() => {
                      if (last) return;
                      onPlatforms(on ? platforms.filter((p) => p !== value) : [...platforms, value]);
                    }}
                    className={cn(
                      'inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[13px] font-semibold',
                      on ? 'border-fin-blue bg-fin-blue-pale text-fin-blue-deep' : 'border-fin-border-2 bg-fin-surface text-fin-ink hover:bg-fin-recess',
                      disabled && 'cursor-default opacity-60',
                    )}
                  >
                    {on ? <CheckIcon className="h-3.5 w-3.5 stroke-[2.5]" aria-hidden="true" /> : null}
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <FieldTitle optional>{ADVANCED_COPY.menus}</FieldTitle>
            <FinDropdown
              block
              size="md"
              mode="checkbox"
              aria-label={ADVANCED_COPY.menus}
              value={menuids}
              label={menuLabel}
              options={menus.map((menu) => ({ value: menu.id, label: menu.name }))}
              placeholder={ADVANCED_COPY.allMenus}
              disabled={disabled || menus.length === 0}
              onChange={onMenus}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdvancedSettings;
